/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CustomerItem } from '../models/CustomerItem';
import type { CustomerRecordItem } from '../models/CustomerRecordItem';
import type { meta } from '../models/meta';
import { request as __request } from '../core/request';

export class CustomerService {

    /**
     * Find customer  by ID.
     * Returns matching record
     * @result CustomerItem successful operation
     * @throws ApiError
     */
    public static async getCustomerById({
        id,
    }: {
        /** Record ID **/
        id: number,
    }): Promise<CustomerItem> {
        const result = await __request({
            method: 'GET',
            path: `/server/customer/${id}`,
            errors: {
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Update an existing customer
     * Update a record based on data given
     * @result CustomerRecordItem Successful operation
     * @throws ApiError
     */
    public static async updateCustomer({
        id,
        requestBody,
    }: {
        /** Record ID **/
        id: number,
        /** Customer object that needs to be updated **/
        requestBody: CustomerRecordItem,
    }): Promise<CustomerRecordItem> {
        const result = await __request({
            method: 'PUT',
            path: `/server/customer/${id}`,
            body: requestBody,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Find multiple customers.  Filterable by name, slug, etc
     * Returns one or more matching records
     * @result any successful operation
     * @throws ApiError
     */
    public static async getCustomerList({
        name,
        slug,
        userId,
        poolId,
        planId,
        grantId,
        tenantId,
        createdBy,
        updatedBy,
        createdStart,
        createdEnd,
        updatedStart,
        updatedEnd,
        activeOnly = true,
        userActiveInclude = 'all',
        page = 1,
        pageSize = 10,
        sort,
    }: {
        /** Find records searching by Name.  Enter ID for specific record **/
        name?: string,
        /** Find records searching by URL Slug **/
        slug?: string,
        /** Find records associated with a given User **/
        userId?: number,
        /** Find records associated with a given Pool **/
        poolId?: number,
        /** Find records associated with a given Plan **/
        planId?: number,
        /** Find records associated with a given Grant **/
        grantId?: number,
        /** Find records associated with a given tenant **/
        tenantId?: number,
        /** Find records created by a given User **/
        createdBy?: number,
        /** Find records updated by a given User **/
        updatedBy?: number,
        /** Start time for range of record creation **/
        createdStart?: number,
        /** End time for range of record creation **/
        createdEnd?: number,
        /** Start time for range of record creation **/
        updatedStart?: number,
        /** End time for range of record creation **/
        updatedEnd?: number,
        /** Records to Include based on Active Flag. **/
        activeOnly?: boolean,
        /** Records to Include based on User Active Flag.  Allows for inactive only if desired. **/
        userActiveInclude?: 'all' | 'active' | 'inactive',
        /** Page to begin results at **/
        page?: number,
        /** Number of results per page **/
        pageSize?: number,
        /** Sort Options - Comma delimited list of key:order. order optional - default ASC  **/
        sort?: string,
    }): Promise<{
        data?: Array<CustomerRecordItem>,
        meta?: meta,
    }> {
        const result = await __request({
            method: 'GET',
            path: `/server/customer/list`,
            query: {
                'name': name,
                'slug': slug,
                'userId': userId,
                'poolId': poolId,
                'planId': planId,
                'grantId': grantId,
                'tenantId': tenantId,
                'createdBy': createdBy,
                'updatedBy': updatedBy,
                'createdStart': createdStart,
                'createdEnd': createdEnd,
                'updatedStart': updatedStart,
                'updatedEnd': updatedEnd,
                'activeOnly': activeOnly,
                'userActiveInclude': userActiveInclude,
                'page': page,
                'pageSize': pageSize,
                'sort': sort,
            },
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Create a new customer
     * Creates a record based on data given
     * @result CustomerRecordItem Successful operation
     * @throws ApiError
     */
    public static async createCustomer({
        requestBody,
    }: {
        /** Customer object to be created **/
        requestBody: CustomerRecordItem,
    }): Promise<CustomerRecordItem> {
        const result = await __request({
            method: 'POST',
            path: `/server/customer`,
            body: requestBody,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Add a user to a customer
     * Creates a record based on data given
     * @result any Successful operation
     * @throws ApiError
     */
    public static async addCustomerUser({
        id,
        userIdPath,
    }: {
        /** Record ID **/
        id: number,
        /** User ID **/
        userIdPath: number,
    }): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/server/customer/${id}/user/${userIdPath}`,
            errors: {
                400: `Error: Bad Request`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Remove an user from a customer
     * Remove a record based on data given
     * @result any Successful operation
     * @throws ApiError
     */
    public static async deleteCustomerUser({
        id,
        userIdPath,
    }: {
        /** Record ID **/
        id: number,
        /** User ID **/
        userIdPath: number,
    }): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/server/customer/${id}/user/${userIdPath}`,
            errors: {
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

}